// components/features/intelligence/LensCard.js — PREMIUM LENS CARD

import { useState } from 'react';
import { getLensColor } from './lensColors';

export default function LensCard({ card, index, onDiscuss, onDismiss }) {
  const [dismissed, setDismissed] = useState(false);
  const [hovered, setHovered] = useState(false);

  if (dismissed) return null;
  const lc = getLensColor(card.lensType);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: '#07070a',
        border: `1px solid ${hovered ? '#2a2a2a' : '#272736'}`,
        borderRadius: 12, overflow: 'hidden',
        transition: 'border-color 0.15s', fontFamily: 'Georgia, serif',
      }}
    >
      {/* Accent bar */}
      <div style={{ height: 3, background: lc.color, opacity: 0.7 }} />

      {/* Header */}
      <div style={{ padding: '14px 18px 12px', borderBottom: '1px solid #141414' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 9, flexWrap: 'wrap', gap: 6 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 5,
            borderRadius: 20, padding: '3px 10px',
            border: `1px solid ${lc.border}`, background: lc.bg,
          }}>
            <div style={{ width: 5, height: 5, borderRadius: '50%', background: lc.color, flexShrink: 0 }} />
            <span style={{ fontSize: 9, color: lc.color, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              {card.lensType}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 10, color: '#2e2e2e' }}>Lens {String(index + 1).padStart(2, '0')}</span>
            <div style={{ width: 1, height: 10, background: '#272736' }} />
            <span style={{ fontSize: 10, color: '#2e2e2e' }}>{card.timeAgo}</span>
          </div>
        </div>
        <h2 style={{ fontSize: 15, color: '#d4d4d4', fontWeight: 'normal', lineHeight: 1.3, marginBottom: 5, fontFamily: 'Georgia, serif' }}>
          {card.title}
        </h2>
        {card.context && (
          <p style={{ fontSize: 11, color: '#333', fontStyle: 'italic', fontFamily: 'Georgia, serif', margin: 0 }}>
            {card.context}
          </p>
        )}
      </div>

      {/* Signal bar */}
      {card.signals && card.signals.length > 0 && (
        <div style={{
          display: 'grid', gridTemplateColumns: `repeat(${card.signals.length}, 1fr)`,
          borderBottom: '1px solid #141414',
        }}>
          {card.signals.map((sig, i) => (
            <div key={i} style={{
              padding: '9px 14px',
              borderRight: i < card.signals.length - 1 ? '1px solid #141414' : 'none',
              display: 'flex', flexDirection: 'column', gap: 2,
            }}>
              <span style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.09em', color: '#2e2e2e' }}>
                {sig.label}
              </span>
              <span style={{ fontSize: 11, fontFamily: 'Georgia, serif', color: sig.color || '#888' }}>
                {sig.value}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Body */}
      <div style={{ padding: '14px 18px' }}>
        {card.bodyPrimary && (
          <p style={{ fontSize: 12, color: '#777', lineHeight: 1.75, marginBottom: 10, fontFamily: 'Georgia, serif', margin: '0 0 10px' }}
            dangerouslySetInnerHTML={{ __html: card.bodyPrimary }}
          />
        )}
        {card.insightPull && (
          <div style={{
            borderLeft: `2px solid ${lc.color}55`, background: `${lc.color}08`,
            padding: '8px 12px', margin: '10px 0',
          }}>
            <p style={{ fontSize: 12, fontStyle: 'italic', lineHeight: 1.65, color: lc.color, fontFamily: 'Georgia, serif', margin: 0 }}>
              &ldquo;{card.insightPull}&rdquo;
            </p>
          </div>
        )}
        {card.bodySecondary && (
          <p style={{ fontSize: 12, color: '#777', lineHeight: 1.75, fontFamily: 'Georgia, serif', margin: 0 }}
            dangerouslySetInnerHTML={{ __html: card.bodySecondary }}
          />
        )}
        {card.tags && card.tags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 10 }}>
            <div style={{
              fontSize: 10, padding: '3px 9px', borderRadius: 20,
              border: `1px solid ${lc.border}`, background: lc.bg, color: lc.color,
            }}>
              {card.lensType}
            </div>
            {card.tags.map((tag, i) => (
              <div key={i} style={{
                fontSize: 10, padding: '3px 9px', borderRadius: 20,
                border: '1px solid #272736', background: '#111', color: '#444',
              }}>
                {tag}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        padding: '10px 18px', borderTop: '1px solid #141414',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 10, color: '#333' }}>Impact</span>
          <div style={{ display: 'flex', gap: 2 }}>
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} style={{
                width: 6, height: 6, borderRadius: '50%',
                background: i <= (card.impact || 3) ? lc.color : '#272736',
              }} />
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 5 }}>
          {onDismiss && (
            <button
              onClick={() => { setDismissed(true); onDismiss(card.id); }}
              style={{
                fontSize: 10, padding: '4px 10px', borderRadius: 5,
                border: '1px solid #272736', background: 'transparent',
                color: '#444', cursor: 'pointer', fontFamily: 'Georgia, serif',
              }}
            >
              Dismiss
            </button>
          )}
          {onDiscuss && (
            <button
              onClick={() => onDiscuss(card)}
              style={{
                fontSize: 10, padding: '4px 10px', borderRadius: 5,
                border: `1px solid ${lc.border}`, background: lc.bg,
                color: lc.color, cursor: 'pointer', fontFamily: 'Georgia, serif',
              }}
            >
              Discuss &#x2192;
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
